import React, { useState, useMemo, useEffect } from 'react';
import { Button, Modal, Form, Select, message, Spin } from 'antd';
import {
  // QuestionCircleOutlined,
  DollarOutlined,
  HomeOutlined,
  PlusOutlined,
  BankOutlined,
} from '@ant-design/icons';
import {
  GoalType,
  calculateFinalDistribution,
  QuestionAssessmentResults,
  Question,
  adjustAllocations,
  AccountType,
  accountTypeToGoalType,
  RetirementType,
} from '@/util/planning-util';
import './step1.scss';
import OpenAccountShell from './OpenAccountShell';
import { useLocation, useNavigate } from 'react-router-dom';
import { Recommendation, TimeHorizonCollection } from '@/util/account-openning-util';
import { selectApexAccounts } from '@/store/features/apex/apex-selectors';
import { fetchApexAccounts } from '@/store/features/apex/apex-thunks';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectRiaProfile } from '@/store/features/userinfo/userinfoSlice';
import type { PendingPlan } from './step2';

interface GoalOption {
  goal: GoalType;
  icon: React.ReactNode;
  disabled?: boolean;
}

interface RetirementOption {
  type: RetirementType;
  title: string;
  description: string;
  disabled?: boolean;
}

type ExistingRetirementTypes = Set<RetirementType>;

type RiskRetakeLocationState = {
  /** Accounts step 2 opened whose allocation hasn't been set yet. */
  createdAccountIds?: string[];
  /** The plan inputs those accounts were opened for: their goals are held now, so not reselected. */
  pendingPlan?: PendingPlan;
  retakeRiskOnly?: boolean;
  returnPath?: string;
  accountId?: string | null;
};

const DEFAULT_RISK_RETAKE_RETURN_PATH = '/home/wealth-profile';

const safeRiskRetakeReturnPath = (returnPath: string | null | undefined): string =>
  returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//')
    ? returnPath
    : DEFAULT_RISK_RETAKE_RETURN_PATH;

interface RiskAnswer {
  id: string;
  answer: string;
  'Points Towards Growth': number;
  'Points Towards capital preservation': number;
  'Points Towards Yield': number;
}

interface RiskQuestion {
  context: string;
  question: string;
  answers: RiskAnswer[];
}

// goal question mapping
const GOAL_QUESTIONS: Record<GoalType, string> = {
  'Just Invest': 'How many years do you plan to invest?',
  Retirement: 'How many years until retirement?',
  'Major Purchase': 'How many years until this purchase?',
  'Supplement Income': 'How many years until income withdrawal?',
  'High Yield Cash': 'How many years until cash withdrawal?',
};

// risk assessment questions
const RISK_QUESTIONS: RiskQuestion[] = [
  {
    context: 'Financial Risk Tolerance',
    question: 'How do you feel about financial risk in your portfolio?',
    answers: [
      {
        id: 'a',
        answer: '🎲 I’m all in — high risk, high reward!',
        'Points Towards Growth': 16.75,
        'Points Towards capital preservation': 0,
        'Points Towards Yield': 0,
      },
      {
        id: 'b',
        answer: '🚀 I’m comfortable with higher risk for potentially greater rewards.',
        'Points Towards Growth': 13.4,
        'Points Towards capital preservation': 2.233333333,
        'Points Towards Yield': 1.116666667,
      },
      {
        id: 'c',
        answer: '⚖️ I’m okay with some risk if it means higher returns.',
        'Points Towards Growth': 10.05,
        'Points Towards capital preservation': 4.466666667,
        'Points Towards Yield': 2.233333333,
      },
      {
        id: 'd',
        answer: '🛡️ I prefer very low risk — modest growth is fine.',
        'Points Towards Growth': 6.7,
        'Points Towards capital preservation': 6.7,
        'Points Towards Yield': 3.35,
      },
      {
        id: 'e',
        answer: '🚫 I avoid risk at all costs — I want stability above all.',
        'Points Towards Growth': 0,
        'Points Towards capital preservation': 11.16666667,
        'Points Towards Yield': 5.583333333,
      },
    ],
  },
  {
    context: 'Comfort With Losses',
    question:
      'If you had $100,000 and it dropped in value, what’s the most loss you could tolerate before feeling the need to make changes?',
    answers: [
      {
        id: 'a',
        answer: '💥 A $50,000 drop (-50%).',
        'Points Towards Growth': 16.75,
        'Points Towards capital preservation': 0,
        'Points Towards Yield': 0,
      },
      {
        id: 'b',
        answer: '📉 A $30,000 drop (-30%).',
        'Points Towards Growth': 13.4,
        'Points Towards capital preservation': 2.233333333,
        'Points Towards Yield': 1.116666667,
      },
      {
        id: 'c',
        answer: '📊 A $15,000 drop (-15%).',
        'Points Towards Growth': 10.05,
        'Points Towards capital preservation': 4.466666667,
        'Points Towards Yield': 2.233333333,
      },
      {
        id: 'd',
        answer: '🪙 A $5,000 drop (-5%).',
        'Points Towards Growth': 6.7,
        'Points Towards capital preservation': 6.7,
        'Points Towards Yield': 3.35,
      },
      {
        id: 'e',
        answer: '💤 Any loss would make me uncomfortable.',
        'Points Towards Growth': 0,
        'Points Towards capital preservation': 11.16666667,
        'Points Towards Yield': 5.583333333,
      },
    ],
  },
  {
    context: 'Behavioral Response to Market Downturns',
    question: 'What did you do with your investments during the 2020 Covid market sell-off?',
    answers: [
      {
        id: 'a',
        answer: '💪 I invested heavily to take advantage of the crash.',
        'Points Towards Growth': 16.75,
        'Points Towards capital preservation': 0,
        'Points Towards Yield': 0,
      },
      {
        id: 'b',
        answer: '📈 I bought a little more while prices were down.',
        'Points Towards Growth': 13.4,
        'Points Towards capital preservation': 2.233333333,
        'Points Towards Yield': 1.116666667,
      },
      {
        id: 'c',
        answer: '😐 I stayed the course and did nothing.',
        'Points Towards Growth': 10.05,
        'Points Towards capital preservation': 4.466666667,
        'Points Towards Yield': 2.233333333,
      },
      {
        id: 'd',
        answer: '😬 I sold some investments but kept most.',
        'Points Towards Growth': 6.7,
        'Points Towards capital preservation': 6.7,
        'Points Towards Yield': 3.35,
      },
      {
        id: 'e',
        answer: '😱 I sold everything to avoid further losses.',
        'Points Towards Growth': 0,
        'Points Towards capital preservation': 11.16666667,
        'Points Towards Yield': 5.583333333,
      },
    ],
  },
  {
    context: 'Fun, General Risk Scenario',
    question: 'You’re given $1,000 to invest. How would you allocate it?',
    answers: [
      {
        id: 'a',
        answer:
          '🎲 Put it all in crypto or a single promising stock for max upside (and downside).',
        'Points Towards Growth': 16.75,
        'Points Towards capital preservation': 0,
        'Points Towards Yield': 0,
      },
      {
        id: 'b',
        answer: '📊 Invest it all in a diversified stock ETF for long-term growth.',
        'Points Towards Growth': 13.4,
        'Points Towards capital preservation': 2.233333333,
        'Points Towards Yield': 1.116666667,
      },
      {
        id: 'c',
        answer: '⚖️ Split it between stocks and bonds: moderate risk, moderate return.',
        'Points Towards Growth': 10.05,
        'Points Towards capital preservation': 4.466666667,
        'Points Towards Yield': 2.233333333,
      },
      {
        id: 'd',
        answer: '🧾 Buy government bonds for safe, predictable returns.',
        'Points Towards Growth': 6.7,
        'Points Towards capital preservation': 6.7,
        'Points Towards Yield': 3.35,
      },
      {
        id: 'e',
        answer: '🏦 Put it all in a high-yield savings account for a guaranteed small gain',
        'Points Towards Growth': 0,
        'Points Towards capital preservation': 11.16666667,
        'Points Towards Yield': 5.583333333,
      },
    ],
  },
];

const Step1: React.FC = () => {
  const dispatch = useAppDispatch();
  const accounts = useAppSelector(selectApexAccounts);
  const riaProfile = useAppSelector(selectRiaProfile);
  // Firm-data is not used to disable goals — fo-apex account_investment_type
  // (JUST_INVEST, RETIREMENT_ROTH, …) is the source of truth for this wizard,
  // independent of whether firm-data is enabled for the firm.
  const shouldIncludeApex =
    Boolean(riaProfile.profile?.apex_correspondent_id) &&
    Boolean(riaProfile.features?.apex_enabled);
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as RiskRetakeLocationState | null;
  const searchParams = new URLSearchParams(location.search);
  const retakeRiskOnly =
    Boolean(routeState?.retakeRiskOnly) || searchParams.get('retakeRiskOnly') === 'true';
  const returnPath = safeRiskRetakeReturnPath(
    routeState?.returnPath ?? searchParams.get('returnPath'),
  );
  const retakeAccountId = routeState?.accountId ?? searchParams.get('accountId') ?? null;

  // Use useMemo to stabilize these computed values
  const existingGoals: Set<GoalType> = useMemo(
    () => new Set(accounts.map((account) => accountTypeToGoalType(account.type as AccountType))),
    [accounts],
  );

  const existingRetirementTypes: ExistingRetirementTypes = useMemo(
    () =>
      new Set(
        accounts
          .filter((a) => a.type.startsWith('RETIREMENT_'))
          .map((a) => a.type.split('_')[1] as RetirementType),
      ),
    [accounts],
  );

  // Hold goal selection until Apex accounts are known so we don't flash
  // Just Invest / retirement subtypes as available, then disable them.
  const [apexGoalsReady, setApexGoalsReady] = useState(
    () => !shouldIncludeApex || accounts.length > 0,
  );

  useEffect(() => {
    if (!shouldIncludeApex) {
      setApexGoalsReady(true);
      return;
    }

    let cancelled = false;

    const markReady = () => {
      if (!cancelled) {
        setApexGoalsReady(true);
      }
    };

    // A failed fetch means we can't tell which Apex goals the investor
    // already has, so retry once before falling back to unlocking the
    // goal list — never leave the page stuck on a spinner, but give the
    // duplicate-goal check a second chance to actually get real data.
    const attemptFetch = (isRetry: boolean) => {
      const request = dispatch(fetchApexAccounts()) as {
        unwrap?: () => Promise<unknown>;
        abort?: () => void;
      };

      if (typeof request?.unwrap === 'function') {
        void request.unwrap().then(markReady, () => {
          if (cancelled) return;
          if (isRetry) {
            console.error('Failed to load Apex accounts for goal locking after retry');
            markReady();
          } else {
            attemptFetch(true);
          }
        });
      } else {
        markReady();
      }

      return request;
    };

    const request = attemptFetch(false);

    return () => {
      cancelled = true;
      if (typeof request?.abort === 'function') {
        request.abort();
      }
    };
  }, [dispatch, shouldIncludeApex]);

  const isFromAddAccount = true;

  const [form] = Form.useForm();
  // const [existingGoalList, setExistingoalList] = useState<Set<GoalType>>(new Set());
  // const [existingRetirementTypeList, setExistingRetirementTypeList] = useState<Set<RetirementType>>(new Set());
  const [currentPhase, setCurrentPhase] = useState(1);
  const [selectedGoals, setSelectedGoals] = useState<Set<GoalType>>(new Set());
  const [selectedRetirementTypes, setSelectedRetirementTypes] = useState<Set<RetirementType>>(
    new Set(),
  );
  const [timeHorizons, setTimeHorizons] = useState<TimeHorizonCollection>({});
  const [riskAnswers, setRiskAnswers] = useState<QuestionAssessmentResults>({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [currentRiskQuestion, setCurrentRiskQuestion] = useState(0);

  // Initialize and sync state variables with computed variables
  useEffect(() => {
    // setExistingoalList(new Set(existingGoals));
    setSelectedGoals(new Set(existingGoals));
  }, [existingGoals]);

  useEffect(() => {
    // setExistingRetirementTypeList(new Set(existingRetirementTypes));
    setSelectedRetirementTypes(new Set(existingRetirementTypes));
  }, [existingRetirementTypes]);

  useEffect(() => {
    if (!retakeRiskOnly) return;
    setCurrentPhase(3);
    setCurrentRiskQuestion(0);
  }, [retakeRiskOnly]);

  const yearOptions = Array.from({ length: 30 }, (_, i) => i + 1);

  const RETIREMENT_TYPE_COUNT = 4;
  // Retirement stays open while any of its account types is still unopened;
  // other goals are opened once their one account exists.
  const isGoalOpened = (goal: GoalType) =>
    goal === 'Retirement'
      ? existingRetirementTypes.size >= RETIREMENT_TYPE_COUNT
      : existingGoals.has(goal);
  // IRA types only count while Retirement itself is checked.
  const newRetirementTypes = () =>
    selectedGoals.has('Retirement')
      ? Array.from(selectedRetirementTypes).filter((type) => !existingRetirementTypes.has(type))
      : [];
  // The selected goals that will open at least one new account.
  const goalsWithNewAccounts = () =>
    Array.from(selectedGoals).filter((goal) =>
      goal === 'Retirement' ? newRetirementTypes().length > 0 : !existingGoals.has(goal),
    );
  const GOAL_ACCOUNT_TYPES: Record<Exclude<GoalType, 'Retirement'>, AccountType> = {
    'Just Invest': 'JUST_INVEST',
    'Major Purchase': 'MAJOR_PURCHASE',
    'Supplement Income': 'SUPPLEMENTAL_INCOME',
    'High Yield Cash': 'HIGH_YIELD_CASH',
  };
  // The Apex account types this selection creates; the confirm summary and
  // the step2 payload both come from here.
  const newAccountTypes = (): AccountType[] =>
    goalsWithNewAccounts().flatMap((goal) =>
      goal === 'Retirement'
        ? newRetirementTypes().map((type) => `RETIREMENT_${type}` as AccountType)
        : [GOAL_ACCOUNT_TYPES[goal]],
    );

  // all retirement types
  const allRetirementTypes: RetirementOption[] = [
    {
      type: 'TRADITIONAL',
      title: 'Traditional IRA',
      description:
        'A tax-deferred retirement account where contributions may be tax-deductible, and withdrawals in retirement are taxed as income.',
    },
    {
      type: 'ROLLOVER',
      title: 'Rollover IRA',
      description:
        "A tax-deferred retirement account that lets you move funds from a previous employer's 401(k) or retirement plan without incurring taxes or penalties.",
    },
    {
      type: 'ROTH',
      title: 'Roth IRA',
      description:
        'A retirement account where contributions are made with after-tax dollars, allowing tax-free growth and tax-free withdrawals in retirement.',
    },
    {
      type: 'TAXABLE',
      title: 'Taxable',
      description:
        'An investment account in which contributions are made with after-tax dollars, and any investment gains are subject to capital gains tax when assets are sold, but with no restrictions on withdrawals.',
    },
  ];

  const availableGoals: GoalOption[] = [
    {
      goal: 'Just Invest',
      icon: <DollarOutlined />,
    },
    {
      goal: 'Retirement',
      icon: <BankOutlined />,
    },
    {
      goal: 'Major Purchase',
      icon: <HomeOutlined />,
    },
    {
      goal: 'Supplement Income',
      icon: <PlusOutlined />,
    },
    {
      goal: 'High Yield Cash',
      icon: <BankOutlined />,
    },
  ];

  // check if ready to save
  const readyToSave = useMemo(() => {
    if (currentPhase === 1) {
      // Back from a failed try: the accounts it opened still need finishing,
      // even with no new goal picked.
      if (routeState?.createdAccountIds?.length) return true;
      // Phase 1: check if goals are selected
      if (isFromAddAccount) {
        // check if any new goals are selected (excluding existing ones)
        const hasNewNonRetirementGoals = Array.from(selectedGoals).some(
          (goal) => goal !== 'Retirement' && !existingGoals.has(goal),
        );

        // check if any new retirement subtypes are selected
        const hasNewRetirementTypes = Array.from(selectedRetirementTypes).some(
          (type) => !existingRetirementTypes.has(type),
        );

        // if Retirement is selected, it must have new retirement subtypes
        // OR there must be new non-retirement goals
        if (selectedGoals.has('Retirement')) {
          // If only retirement is selected, must have new retirement subtypes
          // If retirement + other goals are selected, either new retirement subtypes OR new non-retirement goals
          return hasNewRetirementTypes || hasNewNonRetirementGoals;
        }

        // If no retirement is selected, just check for new non-retirement goals
        return hasNewNonRetirementGoals;
      } else {
        const hasExistingGoal = availableGoals.some(({ disabled }) => disabled);
        const hasValidSelection = selectedGoals.size > 0;
        return hasExistingGoal || hasValidSelection;
      }
    } else if (currentPhase === 2) {
      // Phase 2: every goal on the form, Retirement included, needs a time
      // horizon; step2 builds each new account's inputs from it.
      return goalsWithNewAccounts().every((goal) => (timeHorizons[goal] ?? 0) > 0);
    } else if (currentPhase === 3) {
      // Phase 3: check if the current risk question is answered
      return riskAnswers[currentRiskQuestion] !== undefined;
    }

    return false;
  }, [
    currentPhase,
    selectedGoals,
    selectedRetirementTypes,
    timeHorizons,
    riskAnswers,
    currentRiskQuestion,
    existingGoals,
    existingRetirementTypes,
    isFromAddAccount,
    routeState,
  ]);

  const handleGoalSelect = (goal: GoalType, selected: boolean) => {
    const newSelectedGoals = new Set(selectedGoals);

    if (selected) {
      newSelectedGoals.add(goal);
    } else {
      newSelectedGoals.delete(goal);
      // if Retirement is selected, clear all retirement subtypes
      if (goal === 'Retirement') {
        setSelectedRetirementTypes(new Set());
      }
    }

    setSelectedGoals(newSelectedGoals);
  };

  const handleRetirementTypeSelect = (type: RetirementType, selected: boolean) => {
    if (existingRetirementTypes.has(type)) return; // existing types cannot be selected
    const newSelectedTypes = new Set(selectedRetirementTypes);

    if (selected) {
      newSelectedTypes.add(type);
    } else {
      newSelectedTypes.delete(type);
    }

    setSelectedRetirementTypes(newSelectedTypes);
  };

  const handleTimeHorizonChange = (key: string, value: number) => {
    setTimeHorizons((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRiskAnswerSelect = (questionIndex: number, answerId: string) => {
    setRiskAnswers((prev) => ({
      ...prev,
      [questionIndex]: answerId,
    }));
  };

  const handleBack = () => {
    if (currentPhase === 1) {
      window.history.back();
    } else if (currentPhase === 3 && currentRiskQuestion > 0) {
      // in risk assessment phase, if not the first question, go back to the previous question
      setCurrentRiskQuestion(currentRiskQuestion - 1);
    } else if (currentPhase === 3 && retakeRiskOnly) {
      navigate(returnPath);
    } else {
      setCurrentPhase(currentPhase - 1);
    }
  };

  const handleNext = async () => {
    if (currentPhase === 1) {
      setCurrentPhase(2);
    } else if (currentPhase === 2) {
      setCurrentPhase(3);
      setCurrentRiskQuestion(0); // reset risk question index to 0
    } else if (currentPhase === 3) {
      // check if there are more risk questions
      if (currentRiskQuestion < RISK_QUESTIONS.length - 1) {
        // check if the current question is answered
        if (riskAnswers[currentRiskQuestion] !== undefined) {
          setCurrentRiskQuestion(currentRiskQuestion + 1);
        } else {
          message.error('Please answer the current question before proceeding');
        }
      } else if (retakeRiskOnly) {
        // A retake saves a new risk score: confirm before submitting.
        setIsConfirmModalVisible(true);
      } else {
        // Nothing is created here; accounts open from a later, explicit step.
        void handleConfirmProceed();
      }
    }
  };

  const handleConfirmProceed = async () => {
    setIsConfirmModalVisible(false);

    const selectedGoalsList = goalsWithNewAccounts();
    const accountTypesToCreate = newAccountTypes();

    // Always calculate recommendation from this run's risk assessment answers.
    const dist = calculateFinalDistribution(riskAnswers, RISK_QUESTIONS as Question[]);

    const { preservation, yield: yieldPercentage, growth } = dist;
    const combinedPreservationYield = preservation + yieldPercentage;

    const { growth: growthPercentage, capitalPreservation: preservationPercentage } =
      adjustAllocations(growth, combinedPreservationYield);

    const recommendation: Recommendation = {
      'Capital Preservation': preservationPercentage,
      Growth: growthPercentage,
    };

    if (retakeRiskOnly) {
      const roundedRiskScore = Math.max(1, Math.round(growthPercentage));
      navigate(returnPath, {
        state: {
          retakeRiskScore: roundedRiskScore,
          accountId: retakeAccountId,
        },
      });
      return;
    }

    navigate('/wealth/open-brokerage-account/step2', {
      state: {
        selectedGoals: selectedGoalsList,
        selectedRetirementTypes: newRetirementTypes(),
        accountTypesToCreate,
        timeHorizons,
        recommendation,
        hasExistingAccounts: existingGoals.size > 0,
        ...(routeState?.createdAccountIds?.length
          ? { createdAccountIds: routeState.createdAccountIds, pendingPlan: routeState.pendingPlan }
          : {}),
      },
    });
  };

  const handleConfirmCancel = () => {
    setIsConfirmModalVisible(false);
  };

  // const showModal = () => {
  //   setIsModalVisible(true);
  // };

  const handleModalOk = () => {
    setIsModalVisible(false);
  };

  // The board shows answers without their leading emoji.
  const answerText = (answer: string) => answer.replace(/^\p{Extended_Pictographic}️?\s*/u, '');

  const newAccountNames = useMemo(() => {
    const names: string[] = [];
    availableGoals.forEach(({ goal }) => {
      if (goal === 'Retirement') {
        const newTypes = new Set(newRetirementTypes());
        allRetirementTypes.forEach((option) => {
          if (newTypes.has(option.type)) {
            names.push(option.title);
          }
        });
      } else if (selectedGoals.has(goal) && !existingGoals.has(goal)) {
        names.push(goal);
      }
    });
    return names;
    // availableGoals and allRetirementTypes are constant lists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGoals, selectedRetirementTypes, existingGoals, existingRetirementTypes]);

  const renderChoice = ({
    title,
    detail,
    selected,
    disabled,
    onChange,
    type = 'checkbox',
    name,
  }: {
    title: string;
    detail?: string;
    selected: boolean;
    disabled?: boolean;
    onChange: () => void;
    type?: 'checkbox' | 'radio';
    name?: string;
  }) => (
    <label
      className={`open-account-choice${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}
    >
      <input
        type={type}
        name={name}
        checked={selected}
        disabled={disabled}
        onChange={onChange}
      />
      <span>
        <span className="open-account-choice-title">{title}</span>
        {detail && <span className="open-account-choice-detail">{detail}</span>}
      </span>
    </label>
  );

  const renderGoalOptions = () => (
    <div className="open-account-goals">
      {availableGoals.map((option) => {
        const isSelected = selectedGoals.has(option.goal);
        const alreadyCreated = isGoalOpened(option.goal);
        const card = renderChoice({
          title: alreadyCreated ? `${option.goal} · You already have this account` : option.goal,
          selected: isSelected && !alreadyCreated,
          disabled: alreadyCreated,
          onChange: () => handleGoalSelect(option.goal, !selectedGoals.has(option.goal)),
        });
        const showRetirementTypes = option.goal === 'Retirement' && selectedGoals.has('Retirement');
        if (!showRetirementTypes) {
          return <div key={option.goal}>{card}</div>;
        }
        return (
          <div key={option.goal} className="open-account-goal-group">
            {card}
            <div className="open-account-goal-types" role="group" aria-label="Retirement account types">
              <div className="open-account-group-label">Which kind of retirement account?</div>
              {allRetirementTypes.map((retirementOption) => {
                const typeCreated = existingRetirementTypes.has(retirementOption.type);
                return (
                  <div key={retirementOption.type}>
                    {renderChoice({
                      title: typeCreated
                        ? `${retirementOption.title} · You already have this account`
                        : retirementOption.title,
                      detail: retirementOption.description,
                      selected: selectedRetirementTypes.has(retirementOption.type) && !typeCreated,
                      disabled: typeCreated,
                      onChange: () =>
                        handleRetirementTypeSelect(
                          retirementOption.type,
                          !selectedRetirementTypes.has(retirementOption.type),
                        ),
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {newAccountNames.length > 0 && (
        <p className="open-account-note" role="status">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5M12 8v.01" />
          </svg>
          <span>
            You'll open{' '}
            <strong>
              {newAccountNames.length} new account{newAccountNames.length === 1 ? '' : 's'}
            </strong>
            : {newAccountNames.join(', ')}.
          </span>
        </p>
      )}
    </div>
  );

  const renderTimeHorizonForm = () => {
    const selectedGoalsList = goalsWithNewAccounts();

    return (
      <Form form={form} name="timeHorizon" layout="vertical" className="open-account-horizons">
        {selectedGoalsList.map((goal) => (
          <Form.Item
            key={goal}
            label={`${goal}: ${GOAL_QUESTIONS[goal]}`}
            rules={[
              {
                required: true,
                message: `Please select time horizon for ${goal}`,
              },
            ]}
          >
            <Select
              placeholder="Select years"
              value={timeHorizons[goal]}
              onChange={(value) => handleTimeHorizonChange(goal, value)}
              aria-label={`${goal}: ${GOAL_QUESTIONS[goal]}`}
              options={yearOptions.map((year) => ({
                value: year,
                label: year === 30 ? '30 years or more' : `${year} year${year === 1 ? '' : 's'}`,
              }))}
            />
          </Form.Item>
        ))}
      </Form>
    );
  };

  const currentQuestion = RISK_QUESTIONS[currentRiskQuestion];

  const renderRiskAssessmentForm = () => (
    <fieldset className="open-account-question">
      <legend className="open-account-group-label">{currentQuestion.context}</legend>
      {currentQuestion.answers.map((answer) => (
        <div key={answer.id}>
          {renderChoice({
            type: 'radio',
            name: `risk-${currentRiskQuestion}`,
            title: answerText(answer.answer),
            selected: riskAnswers[currentRiskQuestion] === answer.id,
            onChange: () => handleRiskAnswerSelect(currentRiskQuestion, answer.id),
          })}
        </div>
      ))}
    </fieldset>
  );

  const renderCurrentPhase = () => {
    if (currentPhase === 1) {
      return renderGoalOptions();
    } else if (currentPhase === 2) {
      return renderTimeHorizonForm();
    }
    return renderRiskAssessmentForm();
  };

  const getNextButtonText = () => {
    if (currentPhase === 3) {
      if (currentRiskQuestion < RISK_QUESTIONS.length - 1) {
        return 'Next question';
      }
      if (retakeRiskOnly) return 'Update score';
      return 'Continue';
    }
    return 'Continue';
  };

  const isRisk = currentPhase === 3;
  const title = isRisk
    ? currentQuestion.question
    : currentPhase === 1
      ? 'What are your goals for your money?'
      : 'When will you need this money?';
  const intro =
    currentPhase === 1
      ? 'Select all that apply. Each goal opens its own account.'
      : currentPhase === 2
        ? existingGoals.size > 0
          ? 'Pick a time horizon for each new goal. We will try to reuse allocations from your existing accounts.'
          : 'Pick a time horizon for each goal.'
        : undefined;

  return (
    <>
      <OpenAccountShell
        step={isRisk ? 3 : 2}
        title={title}
        intro={intro}
        meta={isRisk ? `Question ${currentRiskQuestion + 1} of ${RISK_QUESTIONS.length}` : undefined}
        progress={
          isRisk ? (
            <div className="open-account-bars" aria-hidden="true">
              {RISK_QUESTIONS.map((question, index) => (
                <span
                  key={question.question}
                  className={index <= currentRiskQuestion ? 'is-done' : undefined}
                />
              ))}
            </div>
          ) : undefined
        }
        cardLabel={isRisk ? 'Risk question' : currentPhase === 1 ? 'Goals' : 'Time horizon'}
        onExit={retakeRiskOnly ? () => navigate(returnPath) : undefined}
        exitLabel={retakeRiskOnly ? 'Cancel' : undefined}
        // Same steps as step2 shows an existing investor: details on file, then Review.
        onFileSteps={existingGoals.size > 0 ? [1, 4] : undefined}
        stepLabels={existingGoals.size > 0 ? { 5: 'Review' } : undefined}
        footer={
          <>
            <Button onClick={handleBack}>Back</Button>
            <Button
              type="primary"
              onClick={handleNext}
              disabled={!apexGoalsReady || !readyToSave}
            >
              {getNextButtonText()}
            </Button>
          </>
        }
      >
        {apexGoalsReady ? (
          renderCurrentPhase()
        ) : (
          <div className="open-account-loading" data-testid="apex-goals-loading">
            <Spin size="large" />
          </div>
        )}
      </OpenAccountShell>

      <Modal
        title="What are the investment goals?"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        footer={[
          <Button key="got-it" type="primary" onClick={handleModalOk}>
            Got it
          </Button>,
        ]}
        className="goals-modal"
      >
        <div className="modal-content">
          <h3>Just Invest</h3>
          <p>
            A general investment account for building wealth over time without specific restrictions
            or tax advantages.
          </p>

          <h3>Retirement</h3>
          <p>
            Tax-advantaged accounts designed to help you save for retirement, including Traditional
            IRA, Roth IRA, Rollover IRA, and Taxable retirement accounts.
          </p>

          <h3>Major Purchase</h3>
          <p>
            Investment account for saving towards a significant future purchase like a home, car, or
            other major expense.
          </p>

          <h3>Supplement Income</h3>
          <p>
            Investment account focused on generating additional income to supplement your regular
            earnings.
          </p>

          <h3>High Yield Cash</h3>
          <p>
            A cash management account offering higher yields while maintaining liquidity and capital
            preservation.
          </p>
        </div>
      </Modal>

      <Modal
        title="Submit your updated answers?"
        open={isConfirmModalVisible}
        onOk={handleConfirmProceed}
        onCancel={handleConfirmCancel}
        footer={[
          <Button key="cancel" onClick={handleConfirmCancel}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmProceed}>
            Submit
          </Button>,
        ]}
        className="open-account-modal"
      >
        <p>We'll update your risk score with these answers.</p>
      </Modal>
    </>
  );
};

export default Step1;
